import type { HotelReviewDto } from '@pms/shared';
import { EditorialHero } from '../components/landing/EditorialHero';
import { BrandDifference } from '../components/landing/BrandDifference';
import { CuratedHotels } from '../components/landing/CuratedHotels';
import { HowItWorks } from '../components/landing/HowItWorks';
import { TestimonialCarousel } from '../components/landing/TestimonialCarousel';
import { HotelierPitch } from '../components/landing/HotelierPitch';
import { HotelierStories } from '../components/landing/HotelierStories';
import { useHotelReviews } from '../hooks/useHotelReviews';
import type { Testimonial } from '../data/landing';

/** Map a curated hotel-review DTO onto the testimonial card shape (TASK-62). */
function toTestimonial(r: HotelReviewDto): Testimonial {
  return {
    id: r.id,
    quote: r.quote,
    authorName: r.author,
    authorLocation: r.location ?? '',
    avatarUrl: '', // no avatar on curated reviews — card degrades to initials
    rating: r.rating,
  };
}

/**
 * BestAcco homepage (`/`) — Warm Editorial, premium serif.
 *
 * Guest-first: a story-led hero, the BestAcco difference, curated hotels, how it
 * works, and guest voices. Then a strong supply-side hand-off for hoteliers — a
 * conversion pitch and video stories from hosts — closing with a destinations
 * ribbon. Testimonials + hotelier stories read the super-admin-curated feeds
 * (TASK-62) and fall back to static content in src/data/landing.ts when empty.
 */
export default function HomePage() {
  const { data: reviews } = useHotelReviews();
  // Omit the prop entirely when empty so TestimonialCarousel renders its static
  // fallback (exactOptionalPropertyTypes forbids passing `undefined`).
  const testimonials =
    reviews && reviews.length > 0 ? reviews.map(toTestimonial) : null;

  return (
    <>
      <EditorialHero />
      <BrandDifference />
      <CuratedHotels />
      <HowItWorks />
      {testimonials ? (
        <TestimonialCarousel testimonials={testimonials} />
      ) : (
        <TestimonialCarousel />
      )}
      <HotelierPitch />
      <HotelierStories />
    </>
  );
}
